import React, { useState, useEffect, useCallback } from "react";
import { Search, TrendingUp, TrendingDown, MapPin, Building2, Star, Activity, ExternalLink } from "lucide-react";
import { debounce } from "lodash";
import { Link } from "react-router-dom";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://api.littleexplorers.help";

function getHealthScoreColor(score) {
  if (score >= 80) return "text-green-400";
  if (score >= 60) return "text-blue-400";
  if (score >= 40) return "text-yellow-400";
  return "text-red-400";
}

function getHealthScoreBg(score) {
  if (score >= 80) return "bg-green-500";
  if (score >= 60) return "bg-blue-500";
  if (score >= 40) return "bg-yellow-500";
  return "bg-red-500";
}

function CityTable({ cities, title, icon: Icon, isTop }) {
  return (
    <div className="glass-card p-4">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <Icon className={`w-5 h-5 ${isTop ? "text-green-400" : "text-red-400"}`} />
        {title}
      </h3>
      {cities.length === 0 ? (
        <p className="text-gray-400">No data available</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
              <th className="text-left py-2 px-2" style={{ width: "40px" }}>#</th>
              <th className="text-left py-2 px-2">City</th>
              <th className="text-left py-2 px-2">State</th>
              <th className="text-right py-2 px-2">Orgs</th>
              <th className="text-right py-2 px-2">Curated</th>
              <th className="text-right py-2 px-2">% Curated</th>
            </tr>
          </thead>
          <tbody>
            {cities.map((city, index) => (
              <tr
                key={`${city.city}-${city.state}`}
                className="border-b hover:bg-white/5 transition-colors"
                style={{ borderColor: "rgba(255,255,255,0.05)" }}
              >
                <td className="py-2 px-2 text-gray-400 font-medium">
                  {index + 1}
                </td>
                <td className="py-2 px-2 text-white font-medium">
                  {city.city}
                </td>
                <td className="py-2 px-2 text-gray-300">{city.state}</td>
                <td className="py-2 px-2 text-right text-white">
                  {city.organizationCount}
                </td>
                <td className="py-2 px-2 text-right">
                  <span className="text-yellow-400">{city.curatedCount}</span>
                </td>
                <td className="py-2 px-2 text-right">
                  <span className="text-gray-300">
                    {city.organizationCount > 0 
                      ? Math.round((city.curatedCount / city.organizationCount) * 100) 
                      : 0}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function CityDetailView({ cityData, onClose }) {
  if (!cityData) return null;

  return (
    <div className="glass-card p-6 mt-6">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h3 className="text-2xl font-bold text-white flex items-center gap-2">
            <MapPin className="w-6 h-6 text-indigo-400" />
            {cityData.city}, {cityData.state}
          </h3>
          <div className="flex gap-6 mt-2 text-sm">
            <span className="text-gray-400">
              <span className="text-white font-medium">{cityData.organizationCount}</span> organizations
            </span>
            <span className="text-gray-400">
              Average Health Score: <span className={`font-bold ${getHealthScoreColor(cityData.avgHealthScore)}`}>
                {cityData.avgHealthScore}
              </span>
            </span>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors"
        >
          Close
        </button>
      </div>

      <h4 className="text-white font-medium mb-3 flex items-center gap-2">
        <Building2 className="w-4 h-4 text-indigo-400" />
        Organizations
      </h4>

      {cityData.organizations.length === 0 ? (
        <p className="text-gray-400">No organizations found in this city</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b" style={{ borderColor: "rgba(255,255,255,0.1)" }}>
              <th className="text-left py-2 px-3">Organization</th>
              <th className="text-left py-2 px-3">Health Score</th>
              <th className="text-center py-2 px-3">Status</th>
              <th className="text-center py-2 px-3">Curated</th>
              <th className="text-right py-2 px-3">Programs</th>
            </tr>
          </thead>
          <tbody>
            {cityData.organizations.map((org) => (
              <tr
                key={org.id}
                className="border-b hover:bg-white/5 transition-colors"
                style={{ borderColor: "rgba(255,255,255,0.05)" }}
              >
                <td className="py-3 px-3">
                  <Link 
                    to={`/org-management?orgId=${org.id}`}
                    className="text-white font-medium hover:text-indigo-400 transition-colors flex items-center gap-2"
                  >
                    {org.name}
                    <ExternalLink className="w-4 h-4 opacity-50" />
                  </Link>
                </td>
                <td className="py-3 px-3">
                  <div className="flex items-center gap-2">
                    <div className="w-16 bg-gray-700 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${getHealthScoreBg(org.healthScore)}`}
                        style={{ width: `${org.healthScore}%` }}
                      />
                    </div>
                    <span className={`font-bold ${getHealthScoreColor(org.healthScore)}`}>
                      {org.healthScore}
                    </span>
                  </div>
                </td>
                <td className="py-3 px-3 text-center">
                  <span className={`text-xs px-2 py-1 rounded-full capitalize ${
                    org.healthStatus === 'excellent' ? 'bg-green-500/20 text-green-400' :
                    org.healthStatus === 'good' ? 'bg-blue-500/20 text-blue-400' :
                    org.healthStatus === 'fair' ? 'bg-yellow-500/20 text-yellow-400' :
                    org.healthStatus === 'poor' ? 'bg-orange-500/20 text-orange-400' :
                    'bg-red-500/20 text-red-400'
                  }`}>
                    {org.healthStatus}
                  </span>
                </td>
                <td className="py-3 px-3 text-center">
                  {org.isCurated ? (
                    <Star className="w-4 h-4 text-yellow-400 mx-auto fill-yellow-400" />
                  ) : (
                    <span className="text-gray-600">-</span>
                  )}
                </td>
                <td className="py-3 px-3 text-right text-gray-300">
                  {org.programCount}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function CityAnalytics() {
  const [topCities, setTopCities] = useState([]);
  const [bottomCities, setBottomCities] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);

  // Load top and bottom cities on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Load top cities
        const topResponse = await fetch(`${API_BASE_URL}/v2/analytics/cities/top`);
        if (!topResponse.ok) throw new Error("Failed to load top cities");
        const topData = await topResponse.json();
        setTopCities(topData.cities || []);

        // Load bottom cities
        const bottomResponse = await fetch(`${API_BASE_URL}/v2/analytics/cities/bottom`);
        if (!bottomResponse.ok) throw new Error("Failed to load bottom cities");
        const bottomData = await bottomResponse.json();
        setBottomCities(bottomData.cities || []);

        setLoading(false);
      } catch (err) {
        console.error("Error loading city data:", err);
        setError(err.message);
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Search for cities
  const debouncedSearch = useCallback(
    debounce(async (term) => {
      if (!term || term.length < 2) {
        setSearchResults([]);
        return;
      }

      try {
        const response = await fetch(`${API_BASE_URL}/v2/analytics/cities`);
        if (!response.ok) throw new Error("Failed to search cities");
        const data = await response.json();
        
        // Filter cities matching search term
        const filtered = data.cities.filter(
          (city) =>
            city.city.toLowerCase().includes(term.toLowerCase()) ||
            city.state.toLowerCase().includes(term.toLowerCase())
        );
        
        setSearchResults(filtered.slice(0, 10));
      } catch (err) {
        console.error("Error searching cities:", err);
        setSearchResults([]);
      }
    }, 300),
    []
  );

  const handleSearch = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setShowDropdown(true);
    debouncedSearch(term);
  };

  const handleCitySelect = async (city, state) => {
    try {
      setSearchTerm(`${city}, ${state}`);
      setShowDropdown(false);
      
      const response = await fetch(
        `${API_BASE_URL}/v2/analytics/cities/${encodeURIComponent(city)}?state=${encodeURIComponent(state)}`
      );
      if (!response.ok) throw new Error("Failed to load city details");
      const data = await response.json();
      setSelectedCity(data);
    } catch (err) {
      console.error("Error loading city details:", err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-card p-4 text-red-400">
        Error loading city analytics: {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Activity className="w-6 h-6 text-indigo-400" />
          City Analytics
        </h2>
        
        {/* City Search */}
        <div className="relative w-full md:w-96">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search for a city..."
              value={searchTerm}
              onChange={handleSearch}
              onFocus={() => setShowDropdown(true)}
              className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500"
            />
          </div>
          
          {/* Search Dropdown */}
          {showDropdown && searchResults.length > 0 && (
            <div 
              className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto"
              style={{ background: "rgba(30, 30, 40, 0.98)" }}
            >
              {searchResults.map((city) => (
                <button
                  key={`${city.city}-${city.state}`}
                  onClick={() => handleCitySelect(city.city, city.state)}
                  className="w-full px-4 py-3 text-left hover:bg-indigo-600/20 transition-colors border-b border-gray-700 last:border-0"
                >
                  <div className="text-white font-medium">{city.city}</div>
                  <div className="text-gray-400 text-sm">
                    {city.state} • {city.organizationCount} organizations • {city.curatedCount} curated
                  </div>
                </button>
              ))}
            </div>
          )}
          
          {showDropdown && searchTerm.length >= 2 && searchResults.length === 0 && (
            <div 
              className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-600 rounded-lg shadow-lg p-4"
              style={{ background: "rgba(30, 30, 40, 0.98)" }}
            >
              <p className="text-gray-400">No cities found matching "{searchTerm}"</p>
            </div>
          )}
        </div>
      </div>

      {/* City Rankings */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
        <div style={{ flex: 1, minWidth: "400px" }}>
          <CityTable
            cities={topCities}
            title="Top Cities"
            icon={TrendingUp}
            isTop={true}
          />
        </div>
        <div style={{ flex: 1, minWidth: "400px" }}>
          <CityTable
            cities={bottomCities}
            title="Cities Needing Attention"
            icon={TrendingDown}
            isTop={false}
          />
        </div>
      </div>

      {/* Selected City Detail */}
      {selectedCity && (
        <CityDetailView
          cityData={selectedCity}
          onClose={() => {
            setSelectedCity(null);
            setSearchTerm("");
          }}
        />
      )}
    </div>
  );
}

export default CityAnalytics;
